import * as THREE from 'three';
import { mergeGeometries } from 'three/addons/utils/BufferGeometryUtils.js';

const TAU = Math.PI * 2;
const CELEBRATION_SECONDS = 2.4;
const ORDER_CELEBRATION_SECONDS = .85;
const PROJECT_POSITIONS = Object.freeze({
  windchime: { x: -1.7, z: -1.0, width: 1.08, depth: .58, height: 1.42 },
  garden: { x: -6.0, z: -1.3, width: 2.20, depth: 1.45, height: .78 },
  tidepool: { x: -5.8, z: 3.4, width: 1.40, depth: 1.00, height: .96 },
});

function nightAt(hour) {
  const value = Number(hour), h = Number.isFinite(value) ? ((value % 24) + 24) % 24 : 12;
  return h < 12 ? 1 - THREE.MathUtils.smoothstep(h, 5, 7) : THREE.MathUtils.smoothstep(h, 18, 20);
}

function orderStatus(state) {
  const offers = state?.orderBoard?.offers;
  if (!Array.isArray(offers)) return 'done';
  let pending = false;
  for (const order of offers) {
    if (!order || order.completed === true) continue;
    pending = true;
    if (!Array.isArray(order.requirements) || !order.requirements.length) continue;
    let enough = true;
    for (const requirement of order.requirements) {
      const amount = requirement?.quantity, held = state?.inventory?.[requirement?.itemId];
      if (!Number.isSafeInteger(amount) || amount <= 0 || !Number.isSafeInteger(held) || held < amount) { enough = false; break; }
    }
    if (enough) return 'ready';
  }
  return pending ? 'available' : 'done';
}

// These additions use their own small geometries. Approved vegetation, the
// character and saved gameplay state are never changed by this renderer.
export function createIslandLifeVisual(scene, { groundHeightAt = () => 2 } = {}) {
  const decorations = new Map(), entries = [];
  const transform = new THREE.Object3D(), tint = new THREE.Color();
  const opaque = new THREE.MeshStandardMaterial({ vertexColors: true, roughness: .88 });
  const plant = new THREE.MeshStandardMaterial({ color: 0xffffff, roughness: .92 });
  const gold = new THREE.MeshBasicMaterial({ color: 0xffdda0, toneMapped: false });
  const unitPetal = new THREE.SphereGeometry(1, 6, 4);
  const unitStem = new THREE.CylinderGeometry(1, 1, 1, 5);
  const unitSpark = new THREE.IcosahedronGeometry(1, 0);

  function height(x, z) {
    const y = Number(groundHeightAt(x, z));
    return Number.isFinite(y) ? y : 2;
  }
  function rootAt(name, x, z) {
    const root = new THREE.Group(); root.name = name;
    root.position.set(x, height(x, z) + .015, z); scene.add(root); return root;
  }
  function part(parts, geometry, color, position = [0, 0, 0], scale = [1, 1, 1], rotation = [0, 0, 0]) {
    const source = geometry.index ? geometry.toNonIndexed() : geometry;
    if (source !== geometry) geometry.dispose();
    transform.position.set(...position); transform.scale.set(...scale); transform.rotation.set(...rotation); transform.updateMatrix();
    source.applyMatrix4(transform.matrix);
    tint.setHex(color);
    const colors = new Float32Array(source.attributes.position.count * 3);
    for (let i = 0; i < colors.length; i += 3) { colors[i] = tint.r; colors[i + 1] = tint.g; colors[i + 2] = tint.b; }
    source.setAttribute('color', new THREE.BufferAttribute(colors, 3)); parts.push(source);
  }
  function box(parts, size, color, position, rotation) {
    part(parts, new THREE.BoxGeometry(...size), color, position, undefined, rotation);
  }
  function sphere(parts, color, position, scale, rotation) {
    part(parts, new THREE.SphereGeometry(1, 8, 5), color, position, scale, rotation);
  }
  function solid(parent, name, parts) {
    const geometry = mergeGeometries(parts);
    parts.forEach(p => p.dispose());
    const mesh = new THREE.Mesh(geometry, opaque); mesh.name = name;
    mesh.castShadow = mesh.receiveShadow = true; parent.add(mesh); return mesh;
  }
  function shell(parts, position, size = .15, color = 0xf2e4bd) {
    sphere(parts, color, position, [size, size * 1.18, size * .26]);
    for (let i = -2; i <= 2; i++) {
      const angle = i * .23;
      part(parts, new THREE.CylinderGeometry(.006, .008, size * 1.50, 4), 0xd0b17b,
        [position[0] + Math.sin(angle) * size * .35, position[1] + .015, position[2] + size * .26],
        undefined, [0, 0, -angle]);
    }
  }
  function instances(parent, name, geometry, material, count) {
    const mesh = new THREE.InstancedMesh(geometry, material, count); mesh.name = name;
    mesh.instanceMatrix.setUsage(THREE.DynamicDrawUsage); mesh.frustumCulled = false; parent.add(mesh); return mesh;
  }
  function instance(mesh, index, x, y, z, sx, sy, sz, rx = 0, ry = 0, rz = 0) {
    transform.position.set(x, y, z); transform.scale.set(sx, sy, sz); transform.rotation.set(rx, ry, rz); transform.updateMatrix();
    mesh.setMatrixAt(index, transform.matrix);
  }

  const board = rootAt('IslandLife_Board', -2.3, -2.4), boardParts = [];
  board.rotation.y = 2.29;
  board.userData.action = { type: 'island-life' };
  board.userData.footprint = { width: 1.3, depth: .30, height: 1.6 };
  board.userData.orderStatus = 'done'; board.userData.orderCelebrating = false;
  for (const x of [-.50, .50]) {
    box(boardParts, [.095, 1.60, .10], 0x826447, [x, .80, 0]);
    box(boardParts, [.22, .045, .30], 0x735b42, [x, .025, 0]);
  }
  box(boardParts, [1.22, .97, .115], 0xae865c, [0, 1.02, 0]);
  for (const x of [-.29, .29]) box(boardParts, [.018, .92, .009], 0x8c6a49, [x, 1.02, .064]);
  box(boardParts, [1.30, .075, .17], 0x76593d, [0, 1.55, 0]);
  box(boardParts, [1.03, .67, .014], 0xefdbab, [0, 1.015, .067]);
  shell(boardParts, [0, 1.34, .095], .12);
  for (const side of [-1, 1]) {
    sphere(boardParts, 0x739363, [side * .18, 1.36, .095], [.13, .045, .028], [0, 0, side * .44]);
    box(boardParts, [.18, .010, .009], 0x496d4c, [side * .19, 1.36, .125], [0, 0, side * .44]);
  }
  for (const x of [-.34, 0, .34]) {
    box(boardParts, [.20, .025, .018], 0xb7965e, [x, .73, .092]);
    sphere(boardParts, x === 0 ? 0xd89970 : 0x749078, [x, .96, .102], [.075, .115, .025]);
    sphere(boardParts, 0xd1b06f, [x, 1.09, .104], [.028, .028, .020]);
  }
  solid(board, 'IslandLife_BoardWood', boardParts);
  const boardProxy = new THREE.Mesh(new THREE.BoxGeometry(1.30, 1.60, .30),
    new THREE.MeshBasicMaterial({ transparent: true, opacity: 0, depthWrite: false }));
  boardProxy.name = 'IslandLife_BoardHitProxy'; boardProxy.position.y = .80; boardProxy.userData.hitProxy = true; board.add(boardProxy);

  // The letter belongs to the same physical board and picker. It describes
  // current game-day offers without adding unread state or another action.
  const letter = new THREE.Group(); letter.name = 'IslandLife_OrderLetter';
  letter.position.set(.435, 1.37, .122); letter.visible = false; board.add(letter);
  const letterParts = [];
  box(letterParts, [.30, .22, .022], 0xf1dfba, [0, 0, 0]);
  box(letterParts, [.295, .009, .008], 0xc2a36b, [0, -.103, .016]);
  for (const side of [-1, 1]) box(letterParts, [.15, .009, .009], 0xba9b66, [side * .058, .025, .016], [0, 0, side * .56]);
  solid(letter, 'IslandLife_OrderEnvelope', letterParts);
  const sealMaterial = new THREE.MeshBasicMaterial({ color: 0xb29060, toneMapped: false });
  const seal = new THREE.Mesh(new THREE.SphereGeometry(.045, 8, 5), sealMaterial);
  seal.name = 'IslandLife_OrderSeal'; seal.position.set(0, -.025, .021); seal.scale.set(1, .90, .30); letter.add(seal);
  const orderSparks = instances(board, 'IslandLife_OrderCelebration', unitSpark, gold, 8); orderSparks.visible = false;
  let orderPending = false, orderStartedAt = null;

  function decoration(id) {
    const p = PROJECT_POSITIONS[id], root = rootAt(`IslandLife_${id}`, p.x, p.z);
    root.visible = false;
    root.userData = { projectId: id, completed: false, celebrating: false, nightStrength: 0,
      footprint: { width: p.width, depth: p.depth, height: p.height }, groundPoint: { x: p.x, z: p.z } };
    const sparks = instances(root, `IslandLife_${id}_Celebration`, unitSpark, gold, 14);
    sparks.visible = false;
    const item = { id, root, sparks, pending: false, startedAt: null };
    entries.push(item); decorations.set(id, root); return item;
  }

  const wind = decoration('windchime'), bamboo = [];
  for (const x of [-.42, .42]) {
    part(bamboo, new THREE.CylinderGeometry(.030, .040, 1.32, 7), 0x9da370, [x, .66, 0]);
    for (const y of [.31, .67, 1.02]) part(bamboo, new THREE.CylinderGeometry(.040, .040, .025, 7), 0xb2b281, [x, y, 0]);
  }
  part(bamboo, new THREE.CylinderGeometry(.034, .034, 1.03, 7), 0xa9a575, [0, 1.32, 0], undefined, [0, 0, Math.PI / 2]);
  solid(wind.root, 'IslandLife_BambooFrame', bamboo);
  wind.chimes = [];
  for (let i = 0; i < 3; i++) {
    const chime = new THREE.Group(); chime.name = `IslandLife_Chime_${i}`; chime.position.set((i - 1) * .29, 1.30, .01); wind.root.add(chime);
    const pieces = [], length = .28 + i * .07;
    part(pieces, new THREE.CylinderGeometry(.006, .006, length, 4), 0xbfa77c, [0, -length / 2, 0]);
    shell(pieces, [0, -length - .105, 0], .095 + i * .01);
    solid(chime, `IslandLife_ChimeShell_${i}`, pieces); wind.chimes.push(chime);
  }

  const garden = decoration('garden'), planter = [];
  for (const z of [-.68, .68]) box(planter, [2.20, .24, .09], 0xa18459, [0, .12, z]);
  for (const x of [-1.055, 1.055]) box(planter, [.09, .24, 1.36], 0x9b7950, [x, .12, 0]);
  for (const x of [-1.04, 1.04]) for (const z of [-.68, .68]) box(planter, [.14, .30, .14], 0x7c694c, [x, .15, z]);
  box(planter, [2.04, .14, 1.25], 0x75664e, [0, .10, 0]);
  solid(garden.root, 'IslandLife_Planter', planter);
  const stems = instances(garden.root, 'IslandLife_FlowerStems', unitStem, plant, 9);
  const leaves = instances(garden.root, 'IslandLife_FlowerLeaves', unitPetal, plant, 18);
  const petals = instances(garden.root, 'IslandLife_FlowerPetals', unitPetal, plant, 45);
  const centres = instances(garden.root, 'IslandLife_FlowerCentres', unitPetal, plant, 9);
  for (let i = 0; i < 9; i++) {
    const x = (i % 3 - 1) * .62 + Math.sin(i * 2) * .06, z = (Math.floor(i / 3) - 1) * .39, h = .27 + (i % 4) * .065;
    instance(stems, i, x, .16 + h / 2, z, .014, h, .014);
    stems.setColorAt(i, tint.setHex(0x628255));
    for (let side = 0; side < 2; side++) {
      instance(leaves, i * 2 + side, x + (side ? .075 : -.075), .23 + h * .28, z, .10, .021, .038, 0, side ? -.5 : .5, side ? .35 : -.35);
      leaves.setColorAt(i * 2 + side, tint.setHex(side ? 0x849b65 : 0x72915d));
    }
    for (let p = 0; p < 5; p++) {
      const a = p * TAU / 5 + i * .4;
      instance(petals, i * 5 + p, x + Math.sin(a) * .080, .18 + h, z + Math.cos(a) * .080, .054, .026, .100, 0, a, .10);
      petals.setColorAt(i * 5 + p, tint.setHex([0xe57e64, 0xffedbd, 0xe9bd5d][i % 3]));
    }
    instance(centres, i, x, .20 + h, z, .045, .030, .045); centres.setColorAt(i, tint.setHex(0xa87839));
  }
  for (const mesh of [stems, leaves, petals, centres]) { mesh.castShadow = mesh.receiveShadow = true; mesh.instanceMatrix.needsUpdate = true; mesh.instanceColor.needsUpdate = true; }
  garden.wings = instances(garden.root, 'IslandLife_ButterflyWings', unitPetal, plant, 6);
  garden.bodies = instances(garden.root, 'IslandLife_ButterflyBodies', unitPetal, plant, 3);
  for (let i = 0; i < 6; i++) garden.wings.setColorAt(i, tint.setHex(i < 2 ? 0xffe6ae : i < 4 ? 0xe9bf72 : 0xe1a38a));
  for (let i = 0; i < 3; i++) garden.bodies.setColorAt(i, tint.setHex(0x77684a));

  const tide = decoration('tidepool'), display = [];
  for (const x of [-.48, .48]) part(display, new THREE.CylinderGeometry(.13, .16, .32, 7), 0x7b7059, [x, .16, 0]);
  box(display, [1.36, .11, .86], 0xb59a6d, [0, .345, 0]);
  for (let i = 0; i < 5; i++) sphere(display, [0x8c9689, 0xa1a797, 0x7c897e][i % 3], [(i - 2) * .25, .095, .36], [.19, .10, .13]);
  shell(display, [-.43, .51, .15], .13);
  shell(display, [-.03, .48, .24], .105, 0xe8d1a4);
  shell(display, [.42, .50, .17], .13);
  part(display, new THREE.CylinderGeometry(.028, .035, .24, 6), 0x8f7752, [.15, .52, -.12]);
  sphere(display, 0x487f77, [.16, .68, -.12], [.28, .13, .10], [0, 0, .12]);
  part(display, new THREE.ConeGeometry(.12, .20, 4), 0xd5ba75, [-.18, .66, -.12], undefined, [0, 0, -Math.PI / 2]);
  part(display, new THREE.ConeGeometry(.065, .12, 4), 0xbea971, [.12, .82, -.12], undefined, [0, 0, -.14]);
  sphere(display, 0x283c38, [.31, .70, -.035], [.018, .018, .012]);
  sphere(display, 0xf2e3b5, [.317, .707, -.026], [.007, .007, .004]);
  // Place the lantern at the camera-facing edge, clear of the fish display.
  const lampX = .46, lampZ = -.26;
  box(display, [.22, .045, .21], 0x796546, [lampX, .44, lampZ]);
  for (const x of [lampX - .085, lampX + .085]) for (const z of [lampZ - .08, lampZ + .08]) part(display, new THREE.CylinderGeometry(.011, .011, .22, 5), 0x9c8153, [x, .57, z]);
  part(display, new THREE.ConeGeometry(.16, .11, 6), 0x857255, [lampX, .75, lampZ]);
  solid(tide.root, 'IslandLife_TideDisplay', display);
  const lampMaterial = new THREE.MeshStandardMaterial({ color: 0xebc984, roughness: .62, emissive: 0xffd287, emissiveIntensity: 0 });
  tide.lamp = new THREE.Mesh(new THREE.SphereGeometry(.09, 8, 5), lampMaterial); tide.lamp.name = 'IslandLife_TideLantern'; tide.lamp.position.set(lampX, .59, lampZ); tide.root.add(tide.lamp);
  tide.motes = instances(tide.root, 'IslandLife_TideNightMotes', unitSpark, gold, 5); tide.motes.visible = false;

  return { board, decorations,
    celebrateOrder() {
      orderPending = true; orderStartedAt = null;
    },
    celebrate(projectId) {
      const item = entries.find(entry => entry.id === projectId);
      if (!item) return false;
      item.pending = true; item.startedAt = null; return true;
    },
    update(state, time, hour) {
      const value = Number(time), t = Number.isFinite(value) ? Math.max(0, value) : 0, night = nightAt(hour);
      const signal = orderStatus(state), ready = signal === 'ready';
      board.userData.orderStatus = signal; letter.visible = signal !== 'done';
      letter.rotation.z = ready ? Math.sin(t * 1.65) * .045 : 0;
      letter.position.y = 1.37 + (ready ? Math.sin(t * 1.4) * .007 : 0);
      sealMaterial.color.setHex(ready ? 0xf3cb76 : 0xb29060);
      if (orderPending) { orderPending = false; orderStartedAt = t; }
      const orderAge = orderStartedAt === null ? ORDER_CELEBRATION_SECONDS : Math.max(0, t - orderStartedAt);
      orderSparks.visible = orderAge < ORDER_CELEBRATION_SECONDS; board.userData.orderCelebrating = orderSparks.visible;
      if (orderSparks.visible) {
        const phase = orderAge / ORDER_CELEBRATION_SECONDS;
        for (let i = 0; i < orderSparks.count; i++) {
          const a = i * TAU / orderSparks.count, spread = .04 + phase * .12, size = .012 * (1 - phase);
          instance(orderSparks, i, .435 + Math.cos(a) * spread, 1.37 + phase * .12 + Math.sin(a) * .05,
            .19, size, size, size);
        }
        orderSparks.instanceMatrix.needsUpdate = true;
      } else orderStartedAt = null;
      for (const item of entries) {
        const complete = state?.islandLife?.projects?.[item.id]?.completed === true;
        item.root.visible = complete; item.root.userData.completed = complete; item.root.userData.nightStrength = night;
        if (!complete) { item.pending = false; item.startedAt = null; item.sparks.visible = false; item.root.userData.celebrating = false; continue; }
        if (item.pending) { item.pending = false; item.startedAt = t; }
        const age = item.startedAt === null ? CELEBRATION_SECONDS : Math.max(0, t - item.startedAt);
        item.sparks.visible = age < CELEBRATION_SECONDS; item.root.userData.celebrating = item.sparks.visible;
        if (item.sparks.visible) {
          const phase = age / CELEBRATION_SECONDS;
          for (let i = 0; i < item.sparks.count; i++) {
            const a = i * TAU / item.sparks.count + age * .55, spread = .20 + phase * .58;
            instance(item.sparks, i, Math.cos(a) * spread, .52 + Math.sin(phase * Math.PI) * .85 + (i % 3) * .045, Math.sin(a) * spread,
              .025 * (1 - phase), .025 * (1 - phase), .025 * (1 - phase));
          }
          item.sparks.instanceMatrix.needsUpdate = true;
        } else item.startedAt = null;
      }
      if (wind.root.visible) for (let i = 0; i < wind.chimes.length; i++) {
        wind.chimes[i].rotation.z = Math.sin(t * 1.75 + i * .80) * .085;
        wind.chimes[i].rotation.x = Math.sin(t * 1.13 + i) * .035;
      }
      garden.wings.visible = garden.bodies.visible = garden.root.visible && night < .70;
      if (garden.wings.visible) {
        for (let i = 0; i < 3; i++) {
          const a = t * .44 + i * TAU / 3, x = Math.cos(a) * .87, z = Math.sin(a * .8 + i) * .47, y = .83 + Math.sin(t * 1.1 + i) * .13;
          instance(garden.bodies, i, x, y, z, .014, .014, .055, 0, a + Math.PI / 2);
          for (let side = 0; side < 2; side++) instance(garden.wings, i * 2 + side, x + (side ? .041 : -.041), y, z,
            .049, .009, .070, 0, a + Math.PI / 2, (side ? 1 : -1) * (.48 + Math.sin(t * 15 + i) * .45));
        }
        garden.wings.instanceMatrix.needsUpdate = garden.bodies.instanceMatrix.needsUpdate = true;
      }
      tide.lamp.material.emissiveIntensity = night * (1.20 + Math.sin(t * 1.3) * .10);
      tide.motes.visible = tide.root.visible && night > .03;
      if (tide.motes.visible) {
        for (let i = 0; i < tide.motes.count; i++) {
          const a = t * .31 + i * TAU / tide.motes.count, s = (.026 + Math.sin(t * 2 + i) * .004) * night;
          instance(tide.motes, i, Math.cos(a) * .57, .60 + Math.sin(t * .70 + i) * .16, Math.sin(a) * .38, s, s, s);
        }
        tide.motes.instanceMatrix.needsUpdate = true;
      }
    },
  };
}
